import React, { lazy, Suspense, useMemo } from "react";
import useDashboardStore from "store/dashboard-store";
import { loadComponentModule } from "components/ComponentType";
import GlobalChartDialog from "components/basic/chart-in-dialog-button/GlobalChartDialog";
import { IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import { ProgressSpinner } from "primereact/progressspinner";
import ErrorBoundary from "../error-boundary/ErrorBoundary";
import { adjustReferenceTime } from "../../utils";
import { ComponentProvider, useComponentStoreContext } from "./ComponentProvider";

interface ComponentContextWrapperProps {
  component: IDashboardComponent;
  tabId: number;
  groupId: string;
}

const ComponentContextWrapper = ({ component, tabId, groupId }: ComponentContextWrapperProps) => {
  const getTabContextConfig = useDashboardStore(state => state.getTabContextConfig);
  const refreshKey = useDashboardStore(state => state.refreshKey);

  const store = useComponentStoreContext();
  const { setComponentId } = store.getState();
  const { setGroupId } = store.getState();

  const tabContextConfig = getTabContextConfig(tabId) || {};

  setComponentId(component.id);
  setGroupId(groupId);

  const referenceTime =
    tabContextConfig.referenceTime || adjustReferenceTime(tabContextConfig.config).referenceTime;
  const from = tabContextConfig.from || adjustReferenceTime(tabContextConfig.config).dateFrom;
  const to = tabContextConfig.to || adjustReferenceTime(tabContextConfig.config).dateTo;

  const mergedConfig = {
    ...component.config,
    referenceTime,
    from,
    to,
  };

  const LazyComponent = useMemo(() => {
    return lazy(async () => {
      const loadedModule = await loadComponentModule(component.type);
      return { default: loadedModule.default };
    });
  }, [component.type]);

  return (
    <ErrorBoundary
      errorMessage={component.type === "NOT_SUPPORTED" ? "Component is not supported" : undefined}
    >
      <Suspense fallback={<ProgressSpinner />}>
        <LazyComponent key={refreshKey} {...mergedConfig} />
      </Suspense>
      <GlobalChartDialog />
    </ErrorBoundary>
  );
};

export default function WrappedComponentContextWrapper(props: ComponentContextWrapperProps) {
  return (
    <ComponentProvider initialState={{ componentId: props.component.id, groupId: props.groupId }}>
      <ComponentContextWrapper {...props} />
    </ComponentProvider>
  );
}
